import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Typography, Form, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  dynamicSet,
  getDeviceBrandList,
  getDeviceBrandListRequest,
  getDeviceList,
  getDeviceListRequest,
  getMyOrganization,
  useDynamicSelector,
} from "@src/redux";
import { startCase } from "lodash";
import { filterOptions } from "@src/helpers/constants";

const { Title, Text } = Typography;
const { Option } = Select;

const DeviceFiltersForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { result: brandList } = useSelector(getDeviceBrandList);
  const [updatedBrandList, setUpdatedBrandList] = useState<any>({
    items: [],
  });
  const { result: deviceListResult } = useSelector(getDeviceList);
  const [form] = Form.useForm();
  const { pagination: devicePagination } =
    useDynamicSelector("device_pagination");
  const { filter: savedFilter } = useDynamicSelector("device_filter");
  const deviceType = Form.useWatch("type", form);
  const brandId = Form.useWatch("brandId", form);
  const status = Form.useWatch("status", form);
  const { result: getMyOrg } = useSelector(getMyOrganization);
  const statusOptions = [
    { name: "All", key: "" },
    { name: "assigned", key: "assigned_to_kit" },
    { name: "unassigned", key: "free" },
  ];
  const [searchValue, setSearchValue] = useState(
    savedFilter?.searchString || ""
  );
  const hasInitialized = useRef(false);
  const initialPagination = { pageLimit: 10, pageNumber: 1 };

  useEffect(() => {
    form.setFieldsValue({
      search: savedFilter?.searchString || "",
      type: savedFilter?.type || "",
      brandId: savedFilter?.brandId || undefined,
      status: savedFilter?.status ?? "",
    });
    setSearchValue(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const buildFilters = (searchString?: string) => ({
    searchString: searchString ?? searchValue,
    type: deviceType || undefined,
    brandId: brandId || undefined,
    status: status ?? "",
  });

  const requestList = (
    filters: Record<string, any>,
    pagination: { pageLimit: number; pageNumber: number }
  ) => {
    dispatch(dynamicSet("device_filter", { filter: filters }));
    dispatch(dynamicSet("device_pagination", { pagination }));
    dispatch(
      getDeviceListRequest({
        filter: filters,
        pagination,
      })
    );
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const pagination =
      devicePagination ||
      deviceListResult?.pagination ||
      initialPagination;
    requestList(buildFilters(savedFilter?.searchString), pagination);
  }, [devicePagination, deviceListResult, savedFilter]);

  useEffect(() => {
    if (!hasInitialized.current) return;
    requestList(buildFilters(), initialPagination);
  }, [deviceType, brandId, status]);

  const triggerSearch = (searchString: string) => {
    requestList(buildFilters(searchString), initialPagination);
  };

  useEffect(() => {
    if (brandList?.items) {
      const newBrandList = [
        { deviceType: "all", name: "All", id: "" },
        ...brandList?.items,
      ];
      setUpdatedBrandList({
        ...brandList,
        items: newBrandList,
      });
    }
  }, [brandList]);

  const handleFormChange = (changedValues: any, allValues: any) => {
    if ("search" in changedValues) return;

    if ("type" in changedValues) {
      form.setFieldsValue({ brandId: undefined, status: "" });
      if (changedValues.type) {
        dispatch(
          getDeviceBrandListRequest({
            filter: { deviceType: changedValues.type },
          })
        );
      } else {
        setUpdatedBrandList({ items: [] });
      }
    }

    if ("brandId" in changedValues) {
      form.setFieldsValue({ status: "" });
    }
  };

  return (
    <div className="filter-container">
      <div>
        <Row className="header-filter" align="middle">
          <Col span={24}>
            <Title level={4} className="client-filters-header-title">
              Filters
            </Title>
          </Col>
        </Row>
        <Form
          layout="vertical"
          onValuesChange={handleFormChange}
          className="common-filters"
          form={form}
        >
          <Row className="filter-row" gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label="Search" className="filter-label" name="search">
                <Input
                  prefix={<SearchOutlined />}
                  className="filter-input"
                  allowClear
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);

                    if (e.target.value === "") {
                      form.setFieldsValue({ search: "" }); 
                      triggerSearch("");
                    }
                  }}
                  onPressEnter={(e) => {
                    form.setFieldsValue({ search: searchValue }); 
                    triggerSearch(searchValue);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Type" name="type">
                <Select
                  defaultValue="All"
                  className="filter-select"
                  options={filterOptions?.deviceTypes}
                  allowClear
                  placeholder={<span style={{ color: "black" }}>All</span>}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Brand" name={"brandId"}>
                <Select
                  defaultValue={"All"}
                  className="filter-select"
                  options={deviceType ? updatedBrandList.items : []}
                  fieldNames={{ label: "name", value: "id" }}
                  allowClear
                  placeholder={<span style={{ color: "black" }}>All</span>}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Status" name="status">
                <Select
                  defaultValue="All"
                  className="filter-select"
                  allowClear
                  placeholder="All"
                  options={statusOptions}
                  fieldNames={{ label: "name", value: "key" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <Row className="footer">
        <Col>
          <Text strong style={{ fontSize: "20px" }}>
            Total
          </Text>
        </Col>
        <Col className="total-value">
          <Text strong style={{ color: "#ffffff", fontSize: "17px" }}>
            {deviceListResult?.pagination?.totalCount|| 0}
          </Text>
        </Col>
      </Row>
    </div>
  );
};

export default DeviceFiltersForm;
